import { useContext, useState, useEffect, useMemo } from "react";
import { useMutation } from "@apollo/client";
import { ApplicationContext } from "../../components/ApplicationProvider";
import Parser from "html-react-parser";
import { UPDATE_CLOUD_ACCESS_TYPE } from "../../graphql/Mutations/Companies";
import { Notifier } from "../../view/Notifier";
import { Container, InfoText, Label, Row, Toggle } from "./DataPrivacyStyles";
import { Role, useCompanyAsAdminQuery } from "../../graphql";
import { UserContext } from "../../components/User/UserProvider";

const DataPrivacy = () => {
    const { t } = useContext(ApplicationContext);
    const { role } = useContext(UserContext);
    const isReadOnlyCompanyUser = role === Role.User;

    const { company, companyLoading } = useCompanyAsAdminQuery();

    const [selected, setSelected] = useState<"default" | "full" | "none" | null>(null);

    // UI → API mapping
    const mapToEnum = {
        default: "DEFAULT",
        full: "FULL",
        none: "NO_ACCESS",
    } as const;

    // API → UI mapping
    const mapFromEnum = useMemo(
        () =>
            ({
                DEFAULT: "default",
                FULL: "full",
                NO_ACCESS: "none",
            } as const),
        []
    );

    const [mutateCloudAccessType] = useMutation(UPDATE_CLOUD_ACCESS_TYPE);

    // Sync initial state from API
    useEffect(() => {
        if (company?.cloudAccessType) {
            const mappedValue = mapFromEnum[company.cloudAccessType as keyof typeof mapFromEnum];

            setSelected(mappedValue ?? "default");
        }
    }, [company?.cloudAccessType, mapFromEnum]);

    const handleChange = (value: "default" | "full" | "none") => {
        if (isReadOnlyCompanyUser) {
            return;
        }

        if (selected === value) {
            return;
        }

        const mappedValue = mapToEnum[value];

        setSelected(value);

        void mutateCloudAccessType({
            variables: {
                cloudAccessType: mappedValue,
            },
            onCompleted: () => {
                Notifier.success(
                    t("data_privacy.cloud_access_update_success_msg", {
                        status: mappedValue,
                    })
                );
            },
            onError: () => {
                Notifier.fail(t("data_privacy.cloud_access_update_error_msg"));

                // Revert UI on failure
                if (company?.cloudAccessType) {
                    const fallback =
                        mapFromEnum[company.cloudAccessType as keyof typeof mapFromEnum];
                    setSelected(fallback ?? "default");
                }
            },
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent, value: "default" | "full" | "none") => {
        if (isReadOnlyCompanyUser) {
            e.preventDefault();
            return;
        }

        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleChange(value);
        }
    };

    const OPTIONS = [
        {
            key: "default",
            labelKey: "data_privacy.default_access",
            testId: "default",
        },
        {
            key: "full",
            labelKey: "data_privacy.full_access",
            testId: "full",
        },
        {
            key: "none",
            labelKey: "data_privacy.no_access",
            testId: "no-access",
        },
    ] as const;

    // Prevent flicker before API value is set
    if (companyLoading || !selected) {
        return null;
    }

    return (
        <Container data-testid="data-privacy-container">
            <InfoText data-testid="data-privacy-info">
                {Parser(t("data_privacy.data_privacy_info"))}
            </InfoText>

            <div
                role="radiogroup"
                aria-label="Cloud Access Type"
                data-testid="data-privacy-radiogroup"
                style={
                    isReadOnlyCompanyUser ? { opacity: 0.45, filter: "grayscale(1)" } : undefined
                }
            >
                {OPTIONS.map(({ key, labelKey, testId }) => (
                    <Row key={key} data-testid={`row-${testId}`}>
                        <Label data-testid={`label-${testId}`}>{Parser(t(labelKey))}</Label>

                        <Toggle
                            role="radio"
                            tabIndex={isReadOnlyCompanyUser ? -1 : 0}
                            data-testid={`toggle-${testId}`}
                            aria-checked={selected === key}
                            aria-disabled={isReadOnlyCompanyUser}
                            active={selected === key}
                            onClick={isReadOnlyCompanyUser ? undefined : () => handleChange(key)}
                            onKeyDown={(e) => handleKeyDown(e, key)}
                            style={
                                isReadOnlyCompanyUser
                                    ? { pointerEvents: "none", cursor: "not-allowed" }
                                    : undefined
                            }
                        />
                    </Row>
                ))}
            </div>
        </Container>
    );
};

export default DataPrivacy;
